/**
 * Vincent B690 (Eric 2026-02-08) — Panneau Synthèse.
 *
 * Une seule synthèse par document (vidéo ou text_doc). Affiche :
 *   - Titre synthétique (éditable inline)
 *   - Body en Markdown (éditable inline, textarea)
 *   - Key takeaways (affichés en liste, non éditables pour l'instant)
 *
 * Actions (calquées sur Concepts) :
 *   - Générer la synthèse (si aucune n'existe)
 *   - Régénérer (remplace l'existante)
 *   - Éditer (bascule le titre + body en mode textarea)
 *   - Sauvegarder / Annuler
 *   - Supprimer (avec confirm)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { libelleSource } from "../../lib/sourceTranscription";
import axios from "axios";
import { cachedGet, invalidateCache } from "../../lib/apiCache";
import { toast } from "sonner";
import { Note, PencilSimple, Trash, ArrowClockwise, FloppyDisk, X, DownloadSimple, CaretDown, Plus, Heart, MagnifyingGlass, CheckCircle, WarningCircle, Checks, BookOpen } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import { contientPassage } from "../../lib/synthesisMatch";  // Tâche 1319
import SynthesisVerifyModal from "./SynthesisVerifyModal";
import SynthesisVerificationHistory from "./SynthesisVerificationHistory";
import { LockPassageButton, LockedPassagesList } from "./SynthesisLocks";
import SynthesisReviewCard from "./SynthesisReviewCard";
import ManualRevisionModal from "./ManualRevisionModal";
import DictionaryGenerationModal from "./DictionaryGenerationModal";  // Tâche 1285

// Rendu Markdown ultra-simple (headings, gras, italique, listes, sauts de ligne).
// Note : on ne dépend pas de react-markdown pour éviter d'ajouter une dep.
function renderMarkdown(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  html = html.replace(/^## (.+)$/gm, '<h3 class="text-sm font-semibold mt-4 mb-2 text-zinc-800">$1</h3>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-zinc-900">$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/^- (.+)$/gm, '<li class="ml-5 list-disc">$1</li>');
  html = html.replace(/(<li[\s\S]*?<\/li>)/g, (m) => `<ul class="my-2">${m}</ul>`);
  html = html.replace(/\n\n/g, '</p><p class="mb-3">');
  html = `<p class="mb-3">${html}</p>`;
  return html;
}

// Vincent B721 (Eric 2026-02-11) — Markdown INLINE (sans block) pour les
// points clés à retenir. Rend **gras** et *italique* uniquement, safe pour
// être injecté dans un <span>. Escape HTML avant traitement (XSS-safe).
function renderInlineMarkdown(md) {
  if (!md) return "";
  let html = String(md)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-zinc-900">$1</strong>');
  html = html.replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, '$1<em>$2</em>');
  html = html.replace(/`([^`]+)`/g, '<code class="text-[12px] px-1 py-0.5 rounded bg-zinc-100 font-mono">$1</code>');
  return html;
}

export default function SynthesisPanel({
  show,
  lang,
  api,
  videoId,
  canEditKnowledge,
  canManageRevisions,
  synthesis,          // KU object of type=synthesis (null if none)
  dictionaryGeneratedAt,  // Tâche 1331 — date de la dernière génération du dictionnaire
  onRefresh,          // callback to reload knowledge_units after mutation
  onOpenTranscript,   // B1108 — ouvre la transcription sur un passage verrouillé
}) {
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  // Vincent B710 (Eric 2026-02-11) — Édition des points clés (key_takeaways)
  // au même titre que le titre et le body. Tableau de strings, add/edit/remove.
  const [takeawaysDraft, setTakeawaysDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(null); // null | "docx" | "pdf"
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Vincent B1035 (Eric 2026-02-26 SPEC STRICK PROD) — Contrôle batch
  // des encadrés de révision (B1033/B1034). État par claim id, avec un
  // fallback global (`revisionsAllOpen`) pour les cards qui n'ont pas
  // encore été togglées individuellement.
  const [revisionOpenById, setRevisionOpenById] = useState({});
  const [revisionsAllOpen, setRevisionsAllOpen] = useState(true);
  // B1121 — Index du paragraphe en cours d'acceptation groupée (bloque le
  // double clic sur CE paragraphe sans geler les autres).
  const [acceptingPara, setAcceptingPara] = useState(null);
  // Vincent B1036 (Eric 2026-02-26 SPEC STRICK PROD) — Révision manuelle
  // depuis une sélection texte dans la synthèse. Écoute `selectionchange`
  // pour afficher un bouton flottant « + Réviser » juste au-dessus de
  // la sélection. Clic → modal avec le passage pré-rempli.
  const [manualRevisionOpen, setManualRevisionOpen] = useState(false);
  const [pendingSelectionText, setPendingSelectionText] = useState("");
  const [selectionButton, setSelectionButton] = useState(null); // { top, left, text } | null
  const synthesisBodyRef = useRef(null);
  // Vincent B713 (Eric 2026-02-11) — Bouton favori cœur pour la synthèse.
  // Les synthèses sont des KUs de type=synthesis, favorisables via
  // POST /api/favorites { type: "knowledge", item_id: synthesis.id }.
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  // Vincent B943 (Eric 2026-02-14 SPEC STRICK PROD) — Modal de vérification
  // factuelle via GPT-5.2 ("Vérifier sur le web").
  const [verifyOpen, setVerifyOpen] = useState(false);
  // Bogue 1195 (Éric 2026-06-25) — Génération du dictionnaire sur clic, à
  // partir de la SYNTHÈSE (français propre, relu) et non plus automatiquement
  // à la sortie de Whisper (qui produisait des termes inadéquats).
  const [dictGenerating, setDictGenerating] = useState(false);
  // Tâche 1285 — Compte rendu nommé des termes détectés (bleu = ajoutés,
  // vert = mis à jour), au lieu d'un toast qui ne donnait que des chiffres.
  const [dictResult, setDictResult] = useState(null);
  // Tâche 1331 (Eric 2026-06-30) — Le bouton « Générer dictionnaire » porte
  // un check vert dès qu'il a tourné, exactement comme « Vérifier sur le
  // web » : sans cette marque, rien ne distingue un document dont les termes
  // sont déjà au dictionnaire d'un document qu'on n'a jamais traité. La date
  // vient de la base (elle survit au rechargement) ; `dictSessionAt` ne sert
  // qu'à l'afficher tout de suite, avant que le parent ait rechargé la vidéo.
  const [dictSessionAt, setDictSessionAt] = useState(null);
  const dictionaryDone = !!(dictSessionAt || dictionaryGeneratedAt);
  // Tâche 1337 (Eric 2026-06-30) — Mots-clés de la synthèse absents du
  // dictionnaire. Le bouton « Générer dictionnaire » savait les ajouter, mais
  // rien n'indiquait qu'il restait quelque chose à ajouter : on relançait à
  // l'aveugle, ou on ne relançait pas du tout. Détection maison côté serveur,
  // sans appel IA (mêmes règles que le scan de jargon nocturne).
  const [missingTerms, setMissingTerms] = useState([]);
  const [missingOpen, setMissingOpen] = useState(false);
  // Tâche 1340 — Ajout d'un mot au dictionnaire depuis le bandeau.
  const [ajoutEnCours, setAjoutEnCours] = useState(null);
  // Tâche 1345 — Ajout de toute la liste en un geste (un seul appel IA).
  const [ajoutLotEnCours, setAjoutLotEnCours] = useState(false);
  // Tâche 1347 — Fiches ajoutées qui attendent encore une relecture.
  const [fichesAValider, setFichesAValider] = useState(0);
  // Vincent B959 (Eric 2026-02-14 — Eric 2026-02-26 SPEC STRICK PROD) —
  // Marque « vérification effectuée » : dès que le fact-check est terminé
  // (0 claim OU 1+ claims), on flippe l'icône loupe en CheckCircle vert.
  // L'ancienne sémantique « vérifié sans erreur » (green uniquement si
  // 0 claim) laissait le user incapable de savoir si le run avait bien
  // tourné quand des erreurs étaient rapportées.
  // Reset (a) au changement de synthèse, (b) après une édition (body/titre
  // change → vérification périmée), (c) après une régénération.
  const [verifiedClean, setVerifiedClean] = useState(false);
  // Vincent B1021 (Eric 2026-02-26 SPEC STRICK PROD) — Compte des claims
  // détectés mais pas encore corrigés. Affiche un badge « N » rouge à
  // côté du check vert du bouton « Vérifier sur le web » pour rappeler
  // à Vincent qu'il reste des corrections à appliquer. Décrémenté à
  // chaque `onApplied` (par appliedCount), reset avec verifiedClean.
  const [unresolvedClaims, setUnresolvedClaims] = useState(0);
  // B1065 (Eric 2026-09-04 SPEC STRICK PROD) — Date de la dernière
  // vérification affichée à côté du check vert. Sans elle, le check dit
  // « ça a été vérifié » sans dire QUAND : une synthèse vérifiée il y a
  // huit mois porte exactement le même badge qu'une vérifiée ce matin.
  // Le backend enregistre déjà `content.verification_at` : la date (et le
  // check) survivent donc au rechargement de la page. `sessionVerifiedAt`
  // ne sert qu'à l'afficher immédiatement, avant que le parent ait
  // rechargé la synthèse.
  const [sessionVerifiedAt, setSessionVerifiedAt] = useState(null);
  // B1072 — Passages verrouillés de la vidéo (survivent à une régénération).
  const [locks, setLocks] = useState([]);
  // Tâche 1187 (Eric 2026-06-25) — Rejet du bandeau « Erreur factuelle
  // réapparue », mémorisé par signature des passages concernés : le bandeau
  // reste écarté tant que c'est la MÊME alerte, et revient si une nouvelle
  // régression apparaît. Stocké localement : c'est un choix d'affichage
  // personnel, pas une donnée de formation.
  const [regressionDismissed, setRegressionDismissed] = useState(false);
  const regressionSignature = (synthesis?.content?.factual_regression?.items || [])
    .map((it) => String(it.original_text || "")).join("|").slice(0, 300);
  const regressionDismissKey = `knoria.regressionDismissed.${videoId}`;
  useEffect(() => {
    if (!regressionSignature) { setRegressionDismissed(false); return; }
    try {
      setRegressionDismissed(
        localStorage.getItem(regressionDismissKey) === regressionSignature);
    } catch {
      setRegressionDismissed(false);
    }
  }, [regressionSignature, regressionDismissKey]);
  const dismissRegressionBanner = () => {
    try { localStorage.setItem(regressionDismissKey, regressionSignature); } catch { /* mode privé */ }
    setRegressionDismissed(true);
  };
  const exportMenuRef = useRef(null);

  // Fermeture du menu Export au click ailleurs
  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [exportMenuOpen]);

  // Vincent B791 (Eric 2026-02-12) — Ne pas écraser les brouillons pendant
  // que l'utilisateur édite. Auparavant, chaque re-render du parent (qui
  // recrée l'objet `synthesis`) rappelait cet effet et remettait le champ
  // à sa valeur d'origine, effaçant chaque frappe de l'utilisateur. Le
  // garde `!editing` fait que la synchro ne s'exécute qu'à l'ouverture
  // initiale ou après une sauvegarde/annulation (qui repassent editing à
  // false), pas pendant la saisie active.
  useEffect(() => {
    if (synthesis && !editing) {
      setTitleDraft(synthesis.title || synthesis.content?.title || "");
      setBodyDraft(synthesis.content?.body || "");
      setTakeawaysDraft(Array.isArray(synthesis.content?.key_takeaways) ? [...synthesis.content.key_takeaways] : []);
    }
  }, [synthesis, editing]);

  // Vincent B713 (Eric 2026-02-11) — Charge l'état du favori pour la synthèse
  // au chargement / changement de synthèse. Silencieux si l'endpoint échoue.
  useEffect(() => {
    if (!synthesis?.id) {
      setIsFavorite(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await cachedGet(axios, `${api}/api/favorites`, { ttl: 30000 });
        const favs = Array.isArray(data) ? data : [];
        if (!cancelled) {
          setIsFavorite(favs.some((f) => f.item_type === "knowledge" && f.item_id === synthesis.id));
        }
      } catch (_) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [synthesis?.id, api]);

  // Vincent B959 (Eric 2026-02-14 SPEC STRICK PROD) — Reset verifiedClean à
  // chaque changement de synthèse (nouveau doc ou régénération) et à chaque
  // édition (le body ou le titre change → vérification périmée).
  useEffect(() => {
    setVerifiedClean(false);
    setUnresolvedClaims(0);  // Vincent B1021 — reset compteur en même temps
    setSessionVerifiedAt(null);  // B1065 — la date de session redevient celle du serveur
  }, [synthesis?.id, synthesis?.content?.body, synthesis?.title, synthesis?.content?.title]);

  // Tâche 1331 (Eric 2026-06-30) — Une nouvelle fusion efface les deux
  // marques en base. L'écran doit suivre : sans ce retour à zéro, la marque
  // affichée pendant la session survivrait à la fusion et affirmerait à tort
  // que le dictionnaire et la vérification portent sur le nouveau texte.
  useEffect(() => {
    if (!dictionaryGeneratedAt) setDictSessionAt(null);
  }, [dictionaryGeneratedAt]);

  // Tâche 1337 — Recalculé à chaque changement du texte de la synthèse et
  // après une génération de dictionnaire (les termes ajoutés doivent quitter
  // la liste tout seuls).
  const chargerTermesManquants = useCallback(async () => {
    if (!synthesis?.id) { setMissingTerms([]); return; }
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/synthesis/missing-terms`,
        { withCredentials: true },
      );
      setMissingTerms(data?.missing || []);
    } catch {
      setMissingTerms([]);
    }
  }, [api, videoId, synthesis?.id]);
  useEffect(() => {
    chargerTermesManquants();
  }, [chargerTermesManquants, synthesis?.content?.body, dictionaryGeneratedAt, dictSessionAt]);

  // Tâche 1347 — Les fiches ajoutées depuis ce bandeau n'entrent pas au
  // dictionnaire sans relecture (bogue 1200). Sans ce rappel, on ajoutait des
  // termes sans jamais savoir qu'ils dormaient dans une file d'attente.
  const chargerFichesAValider = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}/api/jargon/pending`, { withCredentials: true });
      setFichesAValider(data?.count || 0);
    } catch {
      setFichesAValider(0);
    }
  }, [api]);
  useEffect(() => {
    if (synthesis?.id) chargerFichesAValider();
  }, [chargerFichesAValider, synthesis?.id]);

  // Tâche 1340 — Un clic, un court appel IA sur le seul passage où le mot
  // apparaît, et l'entrée est créée en attente de validation (bogue 1200).
  const ajouterTermeAuDictionnaire = async (terme) => {
    if (ajoutEnCours) return;
    setAjoutEnCours(terme);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/synthesis/missing-terms/add`,
        { term: terme },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `« ${terme} » ajouté au dictionnaire, en attente de validation.`
        : `“${terme}” added to the dictionary, awaiting review.`,
        { description: data?.definition });
      setMissingTerms((prev) => prev.filter((t) => t.term !== terme));
      chargerFichesAValider();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message
        || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
      toast.error((lang === "fr" ? "Ajout impossible : " : "Add failed: ") + detail);
    } finally {
      setAjoutEnCours(null);
    }
  };

  // Tâche 1345 — Tout ajouter d'un geste. Un seul appel IA couvre la liste ;
  // le compte rendu est NOMMÉ, parce qu'un « 12 ajoutés » laisse l'utilisateur
  // deviner ce qui est passé à la trappe.
  const ajouterToutAuDictionnaire = async () => {
    if (ajoutLotEnCours || !missingTerms.length) return;
    const lot = missingTerms.slice(0, 15).map((t) => t.term);
    setAjoutLotEnCours(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/synthesis/missing-terms/add-all`,
        { terms: lot },
        { withCredentials: true },
      );
      const ajoutes = (data?.added || []).map((a) => a.term);
      const ecartes = (data?.skipped || []).map((s) => s.term);
      const echecs = (data?.failed || []).map((f) => f.term);
      const details = [
        ajoutes.length ? `${lang === "fr" ? "Ajoutés" : "Added"} : ${ajoutes.join(", ")}` : null,
        ecartes.length ? `${lang === "fr" ? "Déjà au dictionnaire" : "Already there"} : ${ecartes.join(", ")}` : null,
        echecs.length ? `${lang === "fr" ? "Écartés par le lexicographe" : "Left out"} : ${echecs.join(", ")}` : null,
      ].filter(Boolean).join(" · ");
      if (ajoutes.length) {
        toast.success(lang === "fr"
          ? `${ajoutes.length} terme(s) ajouté(s), en attente de validation.`
          : `${ajoutes.length} term(s) added, awaiting review.`, { description: details });
      } else {
        toast.info(lang === "fr" ? "Aucun terme ajouté." : "No term added.",
          { description: details });
      }
      const traites = new Set([...ajoutes, ...ecartes]);
      setMissingTerms((prev) => prev.filter((t) => !traites.has(t.term)));
      chargerFichesAValider();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message
        || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
      toast.error((lang === "fr" ? "Ajout en lot impossible : " : "Batch add failed: ") + detail);
    } finally {
      setAjoutLotEnCours(false);
    }
  };
  useEffect(() => {
    if (!synthesis?.content?.verification_at) {
      setVerifiedClean(false);
      setSessionVerifiedAt(null);
    }
  }, [synthesis?.content?.verification_at]);

  // B1072 — Les verrous sont rattachés à la vidéo : ils restent valides même
  // après une régénération, qui remplace complètement la synthèse.
  const loadLocks = useCallback(async () => {
    if (!videoId) return;
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/synthesis/locked-passages`,
        { withCredentials: true },
      );
      setLocks(data?.locks || []);
    } catch {
      setLocks([]);
    }
  }, [api, videoId]);

  // Bogue 1195 — « Générer dictionnaire » : identifie les mots-clés de la
  // synthèse, enrichit les termes déjà présents (et ajoute ce document à leurs
  // sources), crée les autres avec ce document comme première référence.
  const generateDictionary = async () => {
    if (!videoId || dictGenerating) return;
    // eslint-disable-next-line no-alert
    if (!verificationDone && !window.confirm(lang === "fr"
      ? "Cette synthèse n'a pas encore été vérifiée sur le web.\n\nLes termes générés reprendront donc des informations non validées. Continuer quand même ?"
      : "This synthesis has not been web-verified yet. Generate anyway?")) {
      return;
    }
    setDictGenerating(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/generate-dictionary`,
        {},
        { withCredentials: true },
      );
      const created = data?.terms_created ?? 0;
      const enriched = data?.terms_enriched ?? 0;
      const dup = data?.duplicates_flagged ?? 0;
      // Tâche 1285 — La modale nomme les termes ; le toast ne fait plus que
      // confirmer l'opération.
      setDictResult(data || {});
      // Tâche 1331 — Marque verte immédiate ; la date en base suivra au
      // prochain rechargement de la vidéo.
      setDictSessionAt(new Date().toISOString());
      if (onRefresh) await onRefresh();
      toast.success(lang === "fr"
        ? `Dictionnaire : ${created} terme(s) créé(s), ${enriched} enrichi(s)`
          + (dup ? `, ${dup} doublon(s) potentiel(s) signalé(s)` : "")
        : `Dictionary: ${created} created, ${enriched} enriched`);
    } catch (e) {
      toast.error((lang === "fr" ? "Génération impossible : " : "Generation failed: ")
        + (e?.response?.data?.detail || e.message));
    } finally {
      setDictGenerating(false);
    }
  };

  // Tâche 1285 (Eric 2026-06-30) — Deuxième temps de « Générer dictionnaire » :
  // enrichir les termes DÉJÀ définis que ce document précise. La recherche est
  // un appel IA, donc une dépense : elle part sur un bouton de la fenêtre,
  // jamais à son ouverture. Les appels réseau vivent ici et la fenêtre reste
  // purement d'affichage.
  const chercherEnrichissements = async () => {
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/dictionary-enrichment-proposals`,
        {},
        { withCredentials: true },
      );
      return data;
    } catch (e) {
      toast.error((lang === "fr" ? "Recherche impossible : " : "Search failed: ")
        + (e?.response?.data?.detail || e.message));
      throw e;
    }
  };

  const appliquerEnrichissements = async (entryIds) => {
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/dictionary-enrichment-apply`,
        { entry_ids: entryIds },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `${data?.applied_count ?? 0} terme(s) enrichi(s) — ce document est ajouté à leurs sources.`
        : `${data?.applied_count ?? 0} term(s) enriched.`);
      return data;
    } catch (e) {
      toast.error((lang === "fr" ? "Enrichissement impossible : " : "Enrichment failed: ")
        + (e?.response?.data?.detail || e.message));
      throw e;
    }
  };

  useEffect(() => { loadLocks(); }, [loadLocks]);
  // B1065 — Date de dernière vérification et péremption.
  // Une vérification faite AVANT la dernière édition ne vaut plus rien : le
  // texte a changé depuis. On affiche alors un avertissement ambre au lieu
  // d'un check vert rassurant — un faux « c'est vérifié » est pire que pas
  // de badge du tout.
  const persistedVerifiedAt = synthesis?.content?.verification_at || null;
  const lastVerifiedAt = sessionVerifiedAt || persistedVerifiedAt;
  // B1115 (Eric 2026-09-08) — Seule une modification MANUELLE (« Éditer »)
  // périme la vérification. `content.last_correction_at` marque l'acceptation
  // de révisions issues de cette même vérification : le compter affichait
  // « Vérification périmée (texte modifié depuis) » sur toutes les synthèses
  // révisées — précisément celles qu'on venait de corriger.
  const lastTextChangeAt = synthesis?.last_edited_at || null;
  const verificationStale = !!(
    !sessionVerifiedAt
    && persistedVerifiedAt
    && lastTextChangeAt
    && new Date(lastTextChangeAt) > new Date(persistedVerifiedAt)
  );
  const verificationDone = verifiedClean || (!!lastVerifiedAt && !verificationStale);
  // B1120 (Eric 2026-09-08) — Une synthèse sortie de la liste des périmées par
  // « Vérifiée sans appel IA » n'a PAS été vérifiée factuellement. Afficher
  // « Vérifié le … » ferait croire qu'un fournisseur a validé le contenu.
  const manualVerification =
    !sessionVerifiedAt && synthesis?.content?.verification_method === "manual";
  const formatVerifiedAt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
        dateStyle: "long",
        timeStyle: "short",
      });
    } catch {
      return String(iso);
    }
  };

  // Vincent B1036 (Eric 2026-02-26 SPEC STRICK PROD) — Écoute
  // `selectionchange` global pour détecter une sélection dans le body
  // de la synthèse. Affiche un bouton flottant « + Réviser » juste
  // au-dessus de la sélection. Visible seulement si `canEditKnowledge`
  // (droit d'ajouter des révisions).
  // Bogue 1036 revu (Éric 2026-06-25) — « Cela apparaît de manière instable,
  // c'est trop à droite pour être lisible, et après une première utilisation
  // ce n'est plus accessible. » Trois causes distinctes :
  //
  //  1. INSTABLE — on écoutait `selectionchange`, qui se déclenche à CHAQUE
  //     mouvement de souris pendant le glissé : le bouton sautait, et le
  //     moindre clic ailleurs le faisait disparaître avant qu'on l'atteigne.
  //     On attend maintenant la FIN du geste (`mouseup` / `keyup`).
  //  2. TROP À DROITE — `position: absolute` avec des coordonnées de PAGE :
  //     dès qu'un ancêtre est positionné, le navigateur décale tout. On passe
  //     en `position: fixed` avec les coordonnées de la fenêtre, bornées pour
  //     que le bouton reste toujours entièrement visible.
  //  3. PLUS ACCESSIBLE APRÈS UN USAGE — en resélectionnant le MÊME passage,
  //     `selectionchange` ne se déclenchait pas : plus aucun bouton. Le
  //     `mouseup` le recalcule à chaque relâchement, même sélection ou non.
  useEffect(() => {
    if (!canEditKnowledge) return undefined;
    let timer = null;
    const compute = () => {
      const container = synthesisBodyRef.current;
      if (!container) { setSelectionButton(null); return; }
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelectionButton(null);
        return;
      }
      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        setSelectionButton(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 3) { setSelectionButton(null); return; }
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) { setSelectionButton(null); return; }
      const BTN_HALF = 90;   // demi-largeur approximative du bouton
      const vw = window.innerWidth || 1024;
      const centre = rect.left + rect.width / 2;
      const clamped = Math.max(BTN_HALF + 8, Math.min(vw - BTN_HALF - 8, centre));
      const above = rect.top - 36;
      setSelectionButton({
        top: above > 8 ? above : rect.bottom + 8,   // sous la sélection s'il n'y a pas la place au-dessus
        left: clamped,
        text,
      });
    };
    const onEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(compute, 60);
    };
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("keyup", onEnd);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("keyup", onEnd);
    };
  }, [canEditKnowledge]);

  if (!show) return null;

  const takeaways = synthesis?.content?.key_takeaways || [];
  const wordCount = synthesis?.content?.word_count || (synthesis?.content?.body ? synthesis.content.body.split(/\s+/).length : 0);

  const handleGenerate = async () => {
    const isReplace = !!synthesis;
    // eslint-disable-next-line no-alert
    if (isReplace && !window.confirm(
      lang === "fr"
        ? "Régénérer la synthèse ? La version actuelle sera remplacée."
        : "Regenerate synthesis? Current version will be replaced."
    )) return;
    setGenerating(true);
    try {
      await axios.post(
        `${api}/api/videos/${videoId}/synthesis/generate`,
        {},
        { withCredentials: true, timeout: 120_000 }
      );
      toast.success(lang === "fr" ? "Synthèse générée" : "Synthesis generated");
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message)
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!synthesis) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      lang === "fr"
        ? "Supprimer la synthèse ? Cette action est irréversible."
        : "Delete synthesis? This is irreversible."
    )) return;
    setDeleting(true);
    try {
      await axios.delete(`${api}/api/knowledge/${synthesis.id}`, { withCredentials: true });
      toast.success(lang === "fr" ? "Synthèse supprimée" : "Synthesis deleted");
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message)
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!synthesis) return;
    if (!titleDraft.trim() || !bodyDraft.trim()) {
      toast.error(lang === "fr" ? "Titre et corps requis" : "Title and body required");
      return;
    }
    setSaving(true);
    try {
      // Vincent B5 XLS 2026-08-10 (Eric 2026-02-10) — Utilise le PATCH
      // canonique `/agent/knowledge-units/{id}` qui accepte maintenant le
      // champ `body`. L'endpoint `PUT /knowledge/{id}` n'existe pas côté
      // backend (seul `PUT /knowledge/{id}/review` existe) → 405 Method
      // Not Allowed sur l'ancienne implémentation.
      // Vincent B710 (Eric 2026-02-11) — Passe aussi `key_takeaways` (filtré
      // pour retirer les entrées vides) pour permettre l'édition des points
      // clés au même endroit que le titre/body.
      const cleanTakeaways = takeawaysDraft
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter((s) => s.length > 0);
      await axios.patch(
        `${api}/api/agent/knowledge-units/${synthesis.id}`,
        {
          title: titleDraft.trim(),
          body: bodyDraft,
          key_takeaways: cleanTakeaways,
        },
        { withCredentials: true }
      );
      toast.success(lang === "fr" ? "Synthèse mise à jour" : "Synthesis updated");
      setEditing(false);
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message)
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setTitleDraft(synthesis?.title || synthesis?.content?.title || "");
    setBodyDraft(synthesis?.content?.body || "");
    setTakeawaysDraft(Array.isArray(synthesis?.content?.key_takeaways) ? [...synthesis.content.key_takeaways] : []);
    setEditing(false);
  };

  // Vincent B710 — Helpers pour édition des key_takeaways
  const updateTakeaway = (idx, value) => {
    setTakeawaysDraft((prev) => prev.map((t, i) => (i === idx ? value : t)));
  };
  const addTakeaway = () => {
    setTakeawaysDraft((prev) => [...prev, ""]);
  };
  const removeTakeaway = (idx) => {
    setTakeawaysDraft((prev) => prev.filter((_, i) => i !== idx));
  };

  // Vincent B713 (Eric 2026-02-11) — Bascule le favori sur la synthèse.
  const toggleFavorite = async () => {
    if (!synthesis?.id || favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${api}/api/favorites/knowledge/${synthesis.id}`, { withCredentials: true });
        setIsFavorite(false);
      } else {
        await axios.post(
          `${api}/api/favorites`,
          { type: "knowledge", item_id: synthesis.id },
          { withCredentials: true }
        );
        setIsFavorite(true);
      }
      try { window.dispatchEvent(new CustomEvent("favorites:updated")); } catch (_) { /* silencieux */ }
      invalidateCache("/api/favorites");
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur favori : " : "Favorite error: ") +
          (err?.response?.data?.detail || err.message)
      );
    } finally {
      setFavLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!synthesis) return;
    setExporting(format);
    setExportMenuOpen(false);
    try {
      const resp = await axios.get(
        `${api}/api/knowledge/${synthesis.id}/export?format=${format}`,
        { withCredentials: true, responseType: "blob" }
      );
      // Extract filename from Content-Disposition
      const dispo = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"] || "";
      let filename = `synthese.${format}`;
      const m = dispo.match(/filename="?([^";]+)"?/i);
      if (m) filename = m[1];
      // Trigger download
      const blob = new Blob([resp.data], {
        type: format === "docx"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(
        lang === "fr"
          ? `Téléchargement ${format.toUpperCase()} lancé`
          : `${format.toUpperCase()} download started`
      );
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Export échoué : " : "Export failed: ") +
          (err?.response?.data?.detail || err.message)
      );
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="synthesis-panel">
      {!synthesis ? (
        // ─── Aucune synthèse : bouton Générer ───
        <div className="flex flex-col items-center justify-center py-16 gap-4 border border-dashed border-zinc-300 rounded-lg" data-testid="synthesis-empty-state">
          <Note size={48} className="text-zinc-400" />
          <h2 className="text-lg font-semibold text-zinc-700">
            {lang === "fr" ? "Aucune synthèse générée" : "No synthesis yet"}
          </h2>
          <p className="text-sm text-zinc-500 text-center max-w-md">
            {lang === "fr"
              ? "Générez une synthèse pédagogique (250-500 mots) qui résume les points clés du document."
              : "Generate a pedagogical synthesis (250-500 words) summarizing the document's key points."}
          </p>
          {canEditKnowledge && (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001a75] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="synthesis-generate-btn"
            >
              <Note size={16} />
              {generating
                ? (lang === "fr" ? "Génération en cours…" : "Generating…")
                : (lang === "fr" ? "Générer la synthèse" : "Generate synthesis")}
            </button>
          )}
        </div>
      ) : (
        // ─── Synthèse présente : affichage + édition + actions ───
        <div className="space-y-6" data-testid="synthesis-content">
          {/* Vincent B7 XLS 2026-08-10 (Eric 2026-02-10) — Layout vertical :
              titre pleine largeur, puis actions sur une ligne dédiée en
              dessous. Auparavant tout était en `flex justify-between`, ce
              qui compressait le titre sur les longs libellés.
              B6 XLS 2026-08-10 — Accent teal (`#14b8a6`) sur la bordure et
              le titre pour cohérence avec la palette timeline vidéo
              (TYPE_COLORS.synthesis). */}
          <header
            className="border-l-4 pl-3 pb-4 space-y-3"
            style={{ borderColor: "#14b8a6" }}
          >
            <div className="min-w-0">
              {/* Vincent B713 (Eric 2026-02-11) — Bouton cœur favori à gauche
                  du titre, aligné sur la convention FileExplorer (bug 545 /
                  B588). Sémantique portée par `fill` vs `regular`. */}
              <div className="flex items-start gap-2">
                {synthesis?.id && (
                  <button
                    type="button"
                    onClick={toggleFavorite}
                    disabled={favLoading}
                    className={`mt-1 p-1 rounded transition-colors flex-shrink-0 ${
                      isFavorite
                        ? "text-pink-500 hover:text-pink-600"
                        : "text-zinc-300 hover:text-pink-400"
                    } disabled:opacity-50`}
                    data-testid="synthesis-favorite-btn"
                    title={isFavorite
                      ? (lang === "fr" ? "Retirer des favoris" : "Remove from favorites")
                      : (lang === "fr" ? "Ajouter aux favoris" : "Add to favorites")}
                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                    aria-pressed={isFavorite}
                  >
                    <Heart size={18} weight={isFavorite ? "fill" : "regular"} />
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  {editing ? (
                    <input
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      className="w-full text-xl font-semibold text-zinc-900 border-b border-teal-300 focus:border-teal-600 outline-none py-1"
                      data-testid="synthesis-title-editor"
                      maxLength={200}
                    />
                  ) : (
                    <h2
                      className="text-xl font-semibold"
                      style={{ color: "#0d9488" }}
                      data-testid="synthesis-title"
                    >
                      {synthesis.title || synthesis.content?.title}
                    </h2>
                  )}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span data-testid="synthesis-word-count">{wordCount} {lang === "fr" ? "mots" : "words"}</span>
                {takeaways.length > 0 && (
                  <span>· {takeaways.length} {lang === "fr" ? "points clés" : "takeaways"}</span>
                )}
                {synthesis.updated_at && (
                  <span>· {lang === "fr" ? "MàJ " : "Updated "}{new Date(synthesis.updated_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
                )}
                {/* Vincent B700 (Eric 2026-02-10) — Badge source utilisée pour
                    la génération de la synthèse. Rassure sur la qualité :
                    « Fusionnée » signifie que la transcription corrigée
                    manuellement (jargon FLHQ) a été employée. */}
                {synthesis.content?.transcript_source && (() => {
                  const src = synthesis.content.transcript_source;
                  // Tâche 1421 — le LIBELLÉ vient de la source unique
                  // (`lib/sourceTranscription`). Seuls la couleur et
                  // l'explication restent ici : elles ne servent qu'à cet
                  // écran, et les sortir les éloignerait de ce qu'elles
                  // colorent.
                  const meta = {
                    "fusionnée": { cls: "bg-teal-100 text-teal-800", tip: lang === "fr" ? "Basée sur la transcription fusionnée (corrigée manuellement)" : "Based on merged transcript (manually corrected)" },
                    "whisper": { cls: "bg-blue-100 text-blue-800", tip: lang === "fr" ? "Basée sur Whisper (transcription brute, non fusionnée)" : "Based on Whisper (raw, unmerged)" },
                    "fireflies": { cls: "bg-purple-100 text-purple-800", tip: lang === "fr" ? "Basée sur l'import Fireflies (aucune fusion disponible)" : "Based on Fireflies import (no merge available)" },
                    "whisper-legacy": { cls: "bg-amber-100 text-amber-800", tip: lang === "fr" ? "Vidéo ancienne : transcription legacy (avant refonte pipeline)" : "Legacy transcript (pre-refactor)" },
                    "document_textuel": { cls: "bg-zinc-100 text-zinc-700", tip: lang === "fr" ? "Généré à partir d'un document textuel" : "Generated from text document" },
                  }[src] || { cls: "bg-zinc-100 text-zinc-500", tip: "" };
                  return (
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.cls}`}
                      title={meta.tip}
                      data-testid="synthesis-source-badge"
                    >
                      · {lang === "fr" ? "Source : " : "Source: "}{libelleSource(src, lang)}
                    </span>
                  );
                })()}
              </div>
            </div>
            {canEditKnowledge && (
              <div className="flex flex-wrap gap-2" data-testid="synthesis-actions">
                {editing ? (
                  <>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white text-xs font-medium hover:bg-[#001a75] disabled:opacity-50"
                      data-testid="synthesis-save-btn"
                    >
                      <FloppyDisk size={14} />
                      {saving ? (lang === "fr" ? "Sauvegarde…" : "Saving…") : (lang === "fr" ? "Sauvegarder" : "Save")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                      data-testid="synthesis-cancel-btn"
                    >
                      <X size={14} />
                      {lang === "fr" ? "Annuler" : "Cancel"}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-zinc-50"
                      data-testid="synthesis-edit-btn"
                    >
                      <PencilSimple size={14} />
                      {lang === "fr" ? "Éditer" : "Edit"}
                    </button>
                    {/* Vincent B691 (Eric 2026-02-08) — Menu déroulant Export DOCX / PDF. */}
                    <div className="relative" ref={exportMenuRef}>
                      <button
                        type="button"
                        onClick={() => setExportMenuOpen((v) => !v)}
                        disabled={!!exporting}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                        data-testid="synthesis-export-btn"
                        aria-haspopup="menu"
                        aria-expanded={exportMenuOpen}
                      >
                        <DownloadSimple size={14} />
                        {exporting
                          ? (lang === "fr" ? "Export…" : "Exporting…")
                          : (lang === "fr" ? "Exporter" : "Export")}
                        <CaretDown size={12} />
                      </button>
                      {exportMenuOpen && (
                        <div
                          className="absolute right-0 mt-1 z-10 min-w-[140px] rounded-md border border-zinc-200 bg-white shadow-lg overflow-hidden"
                          role="menu"
                          data-testid="synthesis-export-menu"
                        >
                          <button
                            type="button"
                            onClick={() => handleExport("docx")}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
                            data-testid="synthesis-export-docx"
                          >
                            <DownloadSimple size={14} />
                            {lang === "fr" ? "Fichier Word (.docx)" : "Word file (.docx)"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleExport("pdf")}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left border-t border-zinc-100"
                            data-testid="synthesis-export-pdf"
                          >
                            <DownloadSimple size={14} />
                            {lang === "fr" ? "Fichier PDF (.pdf)" : "PDF file (.pdf)"}
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={generating}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                      data-testid="synthesis-regenerate-btn"
                    >
                      <ArrowClockwise size={14} />
                      {generating
                        ? (lang === "fr" ? "Régénération…" : "Regenerating…")
                        : (lang === "fr" ? "Régénérer" : "Regenerate")}
                    </button>
                    {/* Vincent B943 (Eric 2026-02-14 SPEC STRICK PROD) —
                        Bouton « Vérifier sur le web » : ouvre la modal
                        SynthesisVerifyModal qui appelle GPT-5.2 pour détecter
                        les erreurs factuelles + propose des corrections. */}
                    <button
                      type="button"
                      onClick={() => setVerifyOpen(true)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium ${
                        verificationStale
                          ? "border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
                          : verificationDone
                            ? "border-emerald-400 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                            : "border-blue-300 text-blue-800 bg-blue-50 hover:bg-blue-100"
                      }`}
                      data-testid="synthesis-verify-web-btn"
                      data-verified-clean={verificationDone ? "true" : "false"}
                      data-verified-at={lastVerifiedAt || ""}
                      data-verification-stale={verificationStale ? "true" : "false"}
                      data-unresolved-claims={String(unresolvedClaims)}
                      title={verificationStale
                        ? (lang === "fr"
                            ? `Vérification périmée : la synthèse a été modifiée après la vérification du ${formatVerifiedAt(lastVerifiedAt)}. Relancer la vérification.`
                            : `Stale verification: the synthesis was edited after the ${formatVerifiedAt(lastVerifiedAt)} check. Re-run it.`)
                        : verificationDone
                        ? (unresolvedClaims > 0
                            ? (lang === "fr"
                                ? `Vérification effectuée le ${formatVerifiedAt(lastVerifiedAt)} : ${unresolvedClaims} erreur(s) à corriger. Cliquer pour ouvrir le rapport.`
                                : `Verified on ${formatVerifiedAt(lastVerifiedAt)}: ${unresolvedClaims} error(s) to fix. Click to open the report.`)
                            : (lang === "fr"
                                ? `Vérification effectuée le ${formatVerifiedAt(lastVerifiedAt)}. Cliquer pour ouvrir le rapport ou re-vérifier.`
                                : `Verified on ${formatVerifiedAt(lastVerifiedAt)}. Click to open the report or re-verify.`))
                        : (lang === "fr"
                            ? "Vérifier les affirmations factuelles de la synthèse via GPT-5.2 (chiffres, dates, plafonds fiscaux)."
                            : "Verify factual claims via GPT-5.2 (numbers, dates, tax thresholds).")}
                    >
                      {verificationStale
                        ? <WarningCircle size={14} weight="fill" className="text-amber-700" />
                        : verificationDone
                          ? <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                          : <MagnifyingGlass size={14} />}
                      {lang === "fr" ? "Vérifier sur le web" : "Verify on web"}
                      {/* Vincent B1021 (Eric 2026-02-26 SPEC STRICK PROD) —
                          Badge rouge « N » quand des claims restent à corriger.
                          Visible uniquement si vérification effectuée
                          (verifiedClean=true) ET count > 0. Le badge disparaît
                          au fur et à mesure que Vincent applique les corrections. */}
                      {verificationDone && unresolvedClaims > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none ml-0.5"
                          data-testid="synthesis-verify-web-unresolved-badge"
                          aria-label={lang === "fr"
                            ? `${unresolvedClaims} erreur(s) non corrigée(s)`
                            : `${unresolvedClaims} unresolved error(s)`}
                        >
                          {unresolvedClaims}
                        </span>
                      )}
                    </button>
                    {/* Bogue 1195 (Éric 2026-06-25) — « Générer dictionnaire »,
                        juste à droite de « Vérifier sur le web ». Le
                        dictionnaire ne se construit plus à partir de la
                        transcription Whisper (fautes de français → termes
                        inadéquats) mais de la synthèse, une fois relue. */}
                    <button
                      type="button"
                      onClick={generateDictionary}
                      disabled={dictGenerating}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium disabled:opacity-60 ${
                        dictionaryDone
                          ? "border-emerald-400 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                          : verificationDone
                            ? "border-teal-400 text-teal-800 bg-teal-50 hover:bg-teal-100"
                            : "border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50"
                      }`}
                      data-testid="synthesis-generate-dictionary-btn"
                      data-web-verified={verificationDone ? "true" : "false"}
                      data-dictionary-generated={dictionaryDone ? "true" : "false"}
                      data-dictionary-generated-at={dictSessionAt || dictionaryGeneratedAt || ""}
                      title={dictionaryDone
                        ? (lang === "fr"
                            ? `Dictionnaire déjà généré à partir de cette synthèse le ${formatVerifiedAt(dictSessionAt || dictionaryGeneratedAt)}. Cliquer pour le refaire. Une nouvelle fusion efface cette marque.`
                            : `Dictionary already generated from this synthesis on ${formatVerifiedAt(dictSessionAt || dictionaryGeneratedAt)}. Click to run it again. A new fusion clears this mark.`)
                        : verificationDone
                        ? (lang === "fr"
                            ? "Identifie les mots-clés de cette synthèse : enrichit les termes déjà au dictionnaire (et y ajoute ce document comme source), crée les termes manquants."
                            : "Extract key terms from this synthesis: enrich existing dictionary entries (adding this document as a source), create the missing ones.")
                        : (lang === "fr"
                            ? "Synthèse pas encore vérifiée sur le web : les termes générés reprendront des informations non validées."
                            : "Synthesis not web-verified yet: generated terms may carry unverified facts.")}
                    >
                      {/* Tâche 1331 — Le livre devient un check vert une fois
                          la génération faite, comme « Vérifier sur le web ». */}
                      {dictionaryDone
                        ? <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                        : <BookOpen size={14} />}
                      {dictGenerating
                        ? (lang === "fr" ? "Génération…" : "Generating…")
                        : (lang === "fr" ? "Générer dictionnaire" : "Generate dictionary")}
                      {/* Bogue 1195 — l'avertissement « non vérifiée » tenait
                          sur 80 px de texte et faisait passer le bouton à la
                          ligne suivante : un pictogramme suffit, l'explication
                          reste dans l'infobulle. */}
                      {!verificationDone && !dictionaryDone && (
                        <WarningCircle
                          size={13}
                          weight="fill"
                          className="text-amber-600"
                          data-testid="synthesis-generate-dictionary-unverified-warning"
                        />
                      )}
                    </button>
                    {/* B1065 (Eric 2026-09-04 SPEC STRICK PROD) — Date de la
                        dernière vérification. Un check vert sans date ne dit
                        pas si la synthèse a été vérifiée ce matin ou il y a
                        huit mois. */}
                    {lastVerifiedAt && (
                      <span
                        className={`basis-full text-[11px] ${verificationStale ? "text-amber-700 font-medium" : "text-zinc-500"}`}
                        data-testid="synthesis-verified-at"
                        title={verificationStale
                          ? (lang === "fr"
                              ? "La synthèse a été modifiée après cette vérification : relancer « Vérifier sur le web »."
                              : "The synthesis was edited after this verification: re-run « Verify on web ».")
                          : formatVerifiedAt(lastVerifiedAt)}
                      >
                        {verificationStale
                          ? (lang === "fr"
                              ? `Vérification périmée (texte modifié depuis) · ${formatVerifiedAt(lastVerifiedAt)}`
                              : `Stale verification (text edited since) · ${formatVerifiedAt(lastVerifiedAt)}`)
                          : manualVerification
                            ? (lang === "fr"
                                ? `Relue manuellement le ${formatVerifiedAt(lastVerifiedAt)} (sans appel IA)`
                                : `Manually reviewed on ${formatVerifiedAt(lastVerifiedAt)} (no AI call)`)
                            : (lang === "fr"
                              ? `Vérifié le ${formatVerifiedAt(lastVerifiedAt)}`
                              : `Verified on ${formatVerifiedAt(lastVerifiedAt)}`)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50 disabled:opacity-50"
                      data-testid="synthesis-delete-btn"
                    >
                      <Trash size={14} />
                      {deleting
                        ? (lang === "fr" ? "Suppression…" : "Deleting…")
                        : (lang === "fr" ? "Supprimer" : "Delete")}
                    </button>
                  </>
                )}
              </div>
            )}
          </header>

          {/* Tâche 1337 (Eric 2026-06-30) — Mots-clés de la synthèse qui ne
              sont pas encore au dictionnaire. Sans ce bandeau, « Générer
              dictionnaire » était un pari : on ne savait ni s'il restait des
              termes à ajouter, ni lesquels. */}
          {missingTerms.length > 0 && (
            <div
              className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
              data-testid="synthesis-missing-terms"
              data-missing-count={String(missingTerms.length)}
            >
              <button
                type="button"
                onClick={() => setMissingOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="synthesis-missing-terms-toggle"
              >
                <span className="text-[12px] font-semibold text-amber-900 inline-flex items-center gap-1.5">
                  <BookOpen size={14} weight="bold" />
                  {lang === "fr"
                    ? `${missingTerms.length} mot${missingTerms.length > 1 ? "s" : ""}-clé${missingTerms.length > 1 ? "s" : ""} absent${missingTerms.length > 1 ? "s" : ""} du dictionnaire`
                    : `${missingTerms.length} key term${missingTerms.length > 1 ? "s" : ""} missing from the dictionary`}
                </span>
                <CaretDown
                  size={13}
                  className={`text-amber-800 transition-transform ${missingOpen ? "rotate-180" : ""}`}
                />
              </button>
              {missingOpen && (
                <>
                  <p className="text-[11px] text-amber-800 mt-1.5 leading-snug">
                    {lang === "fr"
                      ? "« Générer dictionnaire » les ajoutera avec une définition. Un terme refusé par un relecteur ne réapparaît pas ici."
                      : "“Generate dictionary” will add them with a definition. A term a reviewer rejected never comes back here."}
                  </p>
                  {/* Tâche 1345 — Tout ajouter d'un geste, en UN seul appel. */}
                  <button
                    type="button"
                    onClick={ajouterToutAuDictionnaire}
                    disabled={ajoutLotEnCours || !!ajoutEnCours}
                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-400 bg-white text-emerald-800 text-[11px] font-semibold hover:bg-emerald-50 disabled:opacity-50"
                    data-testid="synthesis-add-all-terms-btn"
                    title={lang === "fr"
                      ? "Ajouter tous ces mots au dictionnaire en une fois (un seul appel, 15 mots au maximum). Chaque fiche attend ensuite une relecture."
                      : "Add every word at once (a single call, 15 words max). Each entry then awaits review."}
                  >
                    {ajoutLotEnCours
                      ? <ArrowClockwise size={12} className="animate-spin" />
                      : <Plus size={12} weight="bold" />}
                    {ajoutLotEnCours
                      ? (lang === "fr" ? "Ajout en cours…" : "Adding…")
                      : (lang === "fr"
                          ? `Tout ajouter (${Math.min(missingTerms.length, 15)})`
                          : `Add all (${Math.min(missingTerms.length, 15)})`)}
                  </button>
                  {missingTerms.length > 15 && (
                    <span className="ml-2 text-[10px] text-amber-800">
                      {lang === "fr"
                        ? `${missingTerms.length - 15} de plus au prochain geste`
                        : `${missingTerms.length - 15} more on the next batch`}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="synthesis-missing-terms-list">
                    {missingTerms.map((t) => (
                      <span
                        key={t.term}
                        className={`inline-flex items-center gap-1 text-[11px] font-medium pl-2 pr-1 py-0.5 rounded-full border ${
                          t.candidate_status === "pending"
                            ? "bg-white text-zinc-600 border-zinc-300"
                            : "bg-white text-amber-900 border-amber-300"
                        }`}
                        data-testid={`synthesis-missing-term-${t.term}`}
                        title={t.candidate_status === "pending"
                          ? (lang === "fr"
                              ? `Vu ${t.occurrences} fois — déjà proposé au dictionnaire, en attente de validation`
                              : `Seen ${t.occurrences} times — already proposed, awaiting review`)
                          : (lang === "fr"
                              ? `Vu ${t.occurrences} fois dans cette synthèse`
                              : `Seen ${t.occurrences} times in this synthesis`)}
                      >
                        {t.term}
                        <span className="text-[10px] text-zinc-400">×{t.occurrences}</span>
                        {t.candidate_status === "pending" && (
                          <span className="text-[9px] uppercase tracking-wide text-zinc-400">
                            {lang === "fr" ? "proposé" : "proposed"}
                          </span>
                        )}
                        {/* Tâche 1340 (Eric 2026-06-30) — Combler le manque
                            sur place. Avant, signaler un mot absent obligeait
                            à relancer toute la génération du dictionnaire
                            (un appel IA sur le document entier) pour un seul
                            terme. */}
                        <button
                          type="button"
                          onClick={() => ajouterTermeAuDictionnaire(t.term)}
                          disabled={ajoutEnCours === t.term}
                          className="ml-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50"
                          data-testid={`synthesis-add-term-btn-${t.term}`}
                          aria-label={lang === "fr"
                            ? `Ajouter « ${t.term} » au dictionnaire`
                            : `Add “${t.term}” to the dictionary`}
                          title={lang === "fr"
                            ? "Ajouter ce mot au dictionnaire avec une définition rédigée à partir du passage où il apparaît. L'entrée attend ensuite une validation."
                            : "Add this word to the dictionary with a definition written from the passage where it appears. The entry then awaits review."}
                        >
                          {ajoutEnCours === t.term
                            ? <ArrowClockwise size={11} className="animate-spin" />
                            : <Plus size={11} weight="bold" />}
                        </button>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tâche 1347 (Eric 2026-06-30) — Les fiches ajoutées depuis ce
              bandeau n'entrent pas au dictionnaire sans relecture : on le dit,
              avec le chemin pour aller les traiter. */}
          {fichesAValider > 0 && (
            <a
              href="/dashboard/admin/jargon?pending=1"
              className="mx-4 mt-2 flex items-center justify-between gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 hover:bg-zinc-100 transition-colors"
              data-testid="synthesis-pending-entries-link"
              data-pending-count={String(fichesAValider)}
              title={lang === "fr"
                ? "Ouvrir l'écran du dictionnaire pour valider, fusionner ou rejeter ces fiches."
                : "Open the dictionary screen to approve, merge or reject these entries."}
            >
              <span className="text-[12px] text-zinc-700 inline-flex items-center gap-1.5">
                <BookOpen size={13} className="text-zinc-500" />
                {lang === "fr"
                  ? `${fichesAValider} fiche${fichesAValider > 1 ? "s" : ""} ajoutée${fichesAValider > 1 ? "s" : ""} attend${fichesAValider > 1 ? "ent" : ""} une relecture`
                  : `${fichesAValider} added entr${fichesAValider > 1 ? "ies" : "y"} await${fichesAValider > 1 ? "" : "s"} review`}
              </span>
              <span className="text-[11px] font-semibold text-[#002FA7] shrink-0">
                {lang === "fr" ? "Aller valider" : "Go review"}
              </span>
            </a>
          )}

          {/* Body — Markdown ou textarea */}
          {editing ? (
            <textarea
              value={bodyDraft}
              onChange={(e) => setBodyDraft(e.target.value)}
              rows={18}
              className="w-full font-mono text-sm px-3 py-2 border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="synthesis-body-editor"
              placeholder={lang === "fr" ? "Corps de la synthèse en Markdown…" : "Synthesis body in Markdown…"}
            />
          ) : (
            /* Vincent B1033 (Eric 2026-02-26 SPEC STRICK PROD) — Rendu
               inline des révisions du fact-check : chaque passage cité
               (claim.original_text) reçoit un encadré ambré juste après
               le paragraphe qui le contient, avec texte corrigé + date
               + auteur + source web. La version originale RESTE
               EXACTEMENT INTACTE dans le body (Q2=a — traçabilité pure,
               aucun écrasement). Les claims orphelins (dont l'original
               n'apparaît plus dans le body suite à une édition
               post-vérif) sont rendus à la fin de la section. */
            (() => {
              const bodyText = synthesis.content?.body || "";
              const rawClaims = Array.isArray(synthesis.content?.verification_claims)
                ? synthesis.content.verification_claims
                : [];
              const claims = rawClaims
                .filter((c) => c && c.original_text && c.suggested_correction && !c.hidden)
                .map((c, i) => ({
                  ...c,
                  _key: c.id || `claim-${i}`,
                  // Tâche 1319 (Eric 2026-06-30) — On ancre sur le passage
                  // RÉELLEMENT retrouvé dans le corps par le serveur
                  // (`matched_text`) quand il existe : l'extrait du
                  // vérificateur porte souvent une apostrophe typographique
                  // ou des guillemets que la synthèse n'a pas, et la révision
                  // tombait alors dans « passage original introuvable ».
                  _ancre: c.matched_text || c.original_text,
                  // B1113 (Eric 2026-09-08) — Les révisions acceptées AVANT
                  // ce correctif ont réécrit le corps : leur passage
                  // d'origine n'existe plus. On les ancre alors sur le texte
                  // corrigé, sinon elles tombaient dans la section
                  // « passage original introuvable » et Eric perdait le lien
                  // entre la phrase et sa révision.
                  _loweredCorr: (c.suggested_correction || "").toLowerCase(),
                }));
              // Vincent B1039 (Eric 2026-02-26 SPEC STRICK PROD) — Section
              // « Révisions masquées (N) » en fin de synthèse, visible
              // uniquement pour Admin/Fondateur pour retrouver et réafficher
              // les révisions masquées par erreur.
              const hiddenClaims = rawClaims
                .filter((c) => c && c.suggested_correction && c.hidden)
                .map((c, i) => ({ ...c, _key: c.id || `hidden-${i}` }));
              const consumed = new Set();
              const paragraphs = bodyText.split(/\n{2,}/);
              // Vincent B1035 — Helpers d'ouverture par claim (fallback
              // sur `revisionsAllOpen` si pas encore togglé individuellement).
              const isRevisionOpen = (k) =>
                Object.prototype.hasOwnProperty.call(revisionOpenById, k)
                  ? revisionOpenById[k]
                  : revisionsAllOpen;
              const toggleRevision = (k) =>
                setRevisionOpenById((prev) => ({ ...prev, [k]: !isRevisionOpen(k) }));
              const setAllRevisions = (v) => {
                setRevisionsAllOpen(v);
                setRevisionOpenById({});  // Reset overrides — tous suivent le batch.
              };
              // B1121 (Eric 2026-09-08) — Quand la vérification web renvoie
              // trois précisions sur la même phrase, il fallait cliquer trois
              // fois (ouvrir, accepter, refermer) × 3. Un bouton par
              // paragraphe applique d'un coup les révisions EN ATTENTE de ce
              // paragraphe. La confirmation liste les corrections : accepter
              // en bloc sans les relire serait exactement le risque qu'Eric
              // veut éviter sur du contenu conformité.
              const acceptParagraph = async (pending, paraIndex) => {
                const list = pending
                  .map((c, i) => `${i + 1}. « ${(c.original_text || "").slice(0, 90)} »\n   → ${(c.suggested_correction || "").slice(0, 120)}`)
                  .join("\n");
                const ok = await confirmDialog(
                  `Accepter les ${pending.length} révisions de ce paragraphe ?\n\n${list}\n\nElles seront appliquées à la synthèse. Pour les propager aussi aux connaissances (Q&R, scripts…), utilise la fenêtre de vérification.`
                );
                if (!ok) return;
                setAcceptingPara(paraIndex);
                try {
                  const { data } = await axios.post(
                    `${api}/api/videos/${videoId}/synthesis/apply-corrections`,
                    { claim_ids: pending.map((c) => c.id).filter(Boolean), scope: "synthesis" },
                    { withCredentials: true },
                  );
                  toast.success(`${data?.applied_count || 0} révision(s) acceptée(s) dans ce paragraphe.`);
                  if (onRefresh) await onRefresh();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Application impossible");
                } finally {
                  setAcceptingPara(null);
                }
              };
              return (
                <article
                  ref={synthesisBodyRef}
                  className="prose prose-sm max-w-none text-sm text-zinc-800 prose-p:text-sm prose-p:my-2 prose-li:text-sm prose-li:my-0.5 prose-headings:text-sm prose-h2:text-sm prose-h3:text-sm prose-strong:text-zinc-900"
                  data-testid="synthesis-body"
                >
                  {/* B1070 (Eric 2026-09-04 SPEC STRICK PROD) — Régression
                      factuelle : un passage déjà corrigé est réapparu mot pour
                      mot. Bandeau en tête de synthèse, parce qu'une erreur qui
                      revient repart devant les conseillers sans que personne ne
                      s'en aperçoive. */}
                  {(synthesis?.content?.factual_regression?.items || []).length > 0 && !regressionDismissed && (
                    <div
                      className="not-prose relative mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 pr-10"
                      data-testid="factual-regression-banner"
                    >
                      {/* Tâche 1187 (Eric 2026-06-25) — Croix pour écarter le
                          bandeau. Le rejet est mémorisé pour CETTE liste
                          d'erreurs (signature des passages) : si une NOUVELLE
                          erreur factuelle réapparaît plus tard, le bandeau
                          revient — on n'enterre jamais une alerte inédite. */}
                      <button
                        type="button"
                        onClick={dismissRegressionBanner}
                        className="absolute top-2 right-2 p-1 rounded text-amber-700 hover:bg-amber-200 hover:text-amber-900"
                        data-testid="factual-regression-dismiss"
                        aria-label={lang === "fr" ? "Effacer ce bandeau" : "Dismiss this banner"}
                        title={lang === "fr"
                          ? "Effacer ce bandeau (il reviendra si une nouvelle erreur réapparaît)"
                          : "Dismiss this banner (it returns if a new error reappears)"}
                      >
                        <X size={14} />
                      </button>
                      <p className="text-sm font-semibold text-amber-900">
                        {lang === "fr"
                          ? `Erreur factuelle réapparue (${synthesis.content.factual_regression.items.length})`
                          : `Factual error reappeared (${synthesis.content.factual_regression.items.length})`}
                      </p>
                      <p className="text-[11px] text-amber-800 mt-0.5">
                        {lang === "fr"
                          ? "Ce passage avait déjà été corrigé après une vérification, puis il est revenu dans le texte. Re-vérifier après correction."
                          : "This passage was already fixed after a verification, then came back. Re-verify after fixing."}
                      </p>
                      <ul className="mt-2 space-y-2 text-xs text-amber-900">
                        {synthesis.content.factual_regression.items.map((it, i) => (
                          <li key={i} data-testid={`factual-regression-item-${i}`}>
                            <span className="italic">« {it.original_text} »</span>
                            {it.suggested_correction && (
                              <span className="block text-amber-800">
                                {lang === "fr" ? "Correction attendue : " : "Expected fix: "}
                                {it.suggested_correction}
                              </span>
                            )}
                            {it.corrected_at && (
                              <span className="block text-amber-700">
                                {lang === "fr" ? "déjà corrigée le " : "already fixed on "}
                                {formatVerifiedAt(it.corrected_at)}
                                {it.corrected_by ? ` · ${it.corrected_by}` : ""}
                              </span>
                            )}
                            {/* B1072 — Verrouiller depuis le bandeau : c'est
                                le moment où l'on constate que la correction
                                a été écrasée. */}
                            {it.suggested_correction && (
                              <LockPassageButton
                                videoId={videoId}
                                lockedText={it.suggested_correction}
                                originalText={it.original_text || ""}
                                note={it.issue || ""}
                                onLocked={loadLocks}
                                testId={`factual-regression-lock-${i}`}
                                compact
                              />
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Vincent B1035 (Eric 2026-02-26 SPEC STRICK PROD) —
                      Boutons « Tout replier / Tout déployer » en tête
                      de synthèse. Rendus uniquement s'il y a ≥ 2
                      révisions (inutile sinon). */}
                  {claims.length >= 2 && (
                    <div
                      className="not-prose flex items-center gap-2 mb-3 text-[11px]"
                      data-testid="synthesis-revisions-batch-controls"
                    >
                      <span className="text-zinc-500">
                        {claims.length} révision{claims.length > 1 ? "s" : ""} :
                      </span>
                      <button
                        type="button"
                        onClick={() => setAllRevisions(true)}
                        className="px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 font-semibold"
                        data-testid="synthesis-revisions-expand-all-btn"
                        title="Développer toutes les révisions"
                      >
                        Tout déployer
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllRevisions(false)}
                        className="px-2 py-0.5 rounded-full border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 font-semibold"
                        data-testid="synthesis-revisions-collapse-all-btn"
                        title="Replier toutes les révisions"
                      >
                        Tout replier
                      </button>
                    </div>
                  )}
                  {paragraphs.map((para, i) => {
                    const paraLower = para.toLowerCase();
                    const matched = claims.filter(
                      (c) => !consumed.has(c._key)
                        // Tâche 1319 — Comparaison PLIÉE (apostrophes,
                        // guillemets, espaces insécables, « … » / « ... ») :
                        // la comparaison stricte manquait des passages
                        // pourtant identiques à l'œil.
                        && (contientPassage(para, c._ancre)
                            // B1113 — Repli sur le texte corrigé pour les
                            // révisions acceptées à l'ancienne (corps réécrit).
                            || (c.applied && c._loweredCorr && paraLower.includes(c._loweredCorr))),
                    );
                    matched.forEach((m) => consumed.add(m._key));
                    return (
                      <div key={`para-${i}`}>
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(para) }} />
                        {/* B1121 — Seulement à partir de 2 révisions en
                            attente : pour une seule, le bouton de la carte
                            suffit et un second bouton ne ferait qu'encombrer. */}
                        {canEditKnowledge && matched.filter((c) => !c.applied && c.id).length > 1 && (
                          <div className="not-prose mb-2">
                            <button
                              type="button"
                              onClick={() => acceptParagraph(matched.filter((c) => !c.applied && c.id), i)}
                              disabled={acceptingPara === i}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald-600 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`accept-paragraph-${i}`}
                              title="Applique d'un coup toutes les révisions en attente de ce paragraphe, après confirmation"
                            >
                              <Checks size={13} weight="bold" />
                              {acceptingPara === i
                                ? "Application…"
                                : `Tout accepter dans ce paragraphe (${matched.filter((c) => !c.applied && c.id).length})`}
                            </button>
                          </div>
                        )}
                        {matched.map((c, idx) => (
                          <SynthesisReviewCard
                            key={c._key}
                            claim={c}
                            index={idx}
                            // B1113 — Le corps n'est plus réécrit : la carte
                            // indique elle-même si la révision est acceptée
                            // ou en attente. `legacyReplaced` signale les
                            // anciennes acceptations, où le corps affiché
                            // contient DÉJÀ le texte corrigé.
                            legacyReplaced={!!c.applied && !contientPassage(para, c._ancre)}
                            open={isRevisionOpen(c._key)}
                            onToggle={() => toggleRevision(c._key)}
                            canHide={canManageRevisions}
                            videoId={videoId}
                            onHidden={onRefresh}
                            onLocked={loadLocks}
                          />
                        ))}
                      </div>
                    );
                  })}
                  {claims.filter((c) => !consumed.has(c._key)).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-zinc-200 not-prose" data-testid="synthesis-review-orphans">
                      <p className="text-[11px] font-semibold text-amber-900 mb-2">
                        Révisions dont le passage original n'apparaît plus dans le corps (édité depuis la vérification) :
                      </p>
                      {claims.filter((c) => !consumed.has(c._key)).map((c, idx) => (
                        <SynthesisReviewCard
                          key={c._key}
                          claim={c}
                          index={idx}
                          open={isRevisionOpen(c._key)}
                          onToggle={() => toggleRevision(c._key)}
                          canHide={canManageRevisions}
                          videoId={videoId}
                          onHidden={onRefresh}
                          onLocked={loadLocks}
                        />
                      ))}
                    </div>
                  )}
                  {/* Vincent B1039 (Eric 2026-02-26 SPEC STRICK PROD) —
                      Section « Révisions masquées » repliable en fin
                      de synthèse. Rendue seulement pour Admin/Fondateur
                      et s'il y a au moins 1 claim masqué. */}
                  {canManageRevisions && hiddenClaims.length > 0 && (
                    <details
                      className="mt-4 pt-3 border-t border-zinc-200 not-prose"
                      data-testid="synthesis-hidden-revisions-section"
                    >
                      <summary className="cursor-pointer text-[11px] font-semibold text-zinc-600 hover:text-zinc-900">
                        Révisions masquées ({hiddenClaims.length}) — cliquez pour révéler
                      </summary>
                      <ul className="mt-2 space-y-2">
                        {hiddenClaims.map((c) => (
                          <li
                            key={c._key}
                            className="rounded-md border border-zinc-200 bg-zinc-50 dark:bg-zinc-800/40 p-2 text-xs"
                            data-testid={`synthesis-hidden-revision-${c._key}`}
                          >
                            <div className="text-[10px] text-zinc-500 mb-1">
                              Masquée le <span className="font-mono">{c.hidden_at ? new Date(c.hidden_at).toLocaleString("fr-CA") : "—"}</span>
                              {" "}par <span className="font-mono">{c.hidden_by || "?"}</span>
                              {" · "}Ajoutée par <span className="font-mono">{c.verified_by || "?"}</span>
                            </div>
                            {c.original_text && (
                              <blockquote className="text-[11px] italic text-zinc-600 dark:text-zinc-400 border-l-2 border-zinc-400 pl-2 mb-1">
                                « {c.original_text} »
                              </blockquote>
                            )}
                            <p className="font-semibold text-zinc-800 dark:text-zinc-100 mb-1">
                              → {c.suggested_correction}
                            </p>
                            <div className="flex items-center justify-between gap-2">
                              {c.source ? (
                                <a
                                  href={c.source}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[10px] text-blue-700 hover:underline truncate max-w-xs"
                                >
                                  {c.source}
                                </a>
                              ) : <span />}
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await axios.post(
                                      `${api}/api/videos/${videoId}/synthesis/revisions/${c.id}/unhide`,
                                      {},
                                      { withCredentials: true },
                                    );
                                    toast.success("Révision réaffichée.");
                                    if (onRefresh) await onRefresh();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Erreur lors du réaffichage");
                                  }
                                }}
                                className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-500"
                                data-testid={`synthesis-hidden-revision-restore-btn-${c._key}`}
                                title="Réafficher cette révision dans la synthèse"
                              >
                                Réafficher
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </article>
              );
            })()
          )}

          {/* Key takeaways — read-only ou éditable
              Vincent B710 (Eric 2026-02-11) — L'édition des points clés est
              désormais possible : add/edit/remove via inputs. */}
          {editing ? (
            <section className="border-t border-zinc-200 pt-4" data-testid="synthesis-takeaways-editor">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-zinc-700">
                  {lang === "fr" ? "Points clés à retenir" : "Key takeaways"}
                  <span className="ml-2 text-xs text-zinc-400 font-normal">({takeawaysDraft.length})</span>
                </h3>
                <button
                  type="button"
                  onClick={addTakeaway}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-teal-300 text-teal-700 text-xs font-medium hover:bg-teal-50"
                  data-testid="synthesis-takeaway-add-btn"
                >
                  <Plus size={12} />
                  {lang === "fr" ? "Ajouter un point" : "Add takeaway"}
                </button>
              </div>
              {takeawaysDraft.length === 0 ? (
                <p className="text-xs text-zinc-400 italic" data-testid="synthesis-takeaways-empty">
                  {lang === "fr"
                    ? "Aucun point clé. Cliquez sur « Ajouter un point » pour en créer un."
                    : "No takeaways. Click \"Add takeaway\" to create one."}
                </p>
              ) : (
                <ul className="space-y-2">
                  {takeawaysDraft.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2"
                      data-testid={`synthesis-takeaway-editor-${i}`}
                    >
                      <span className="text-[#002FA7] font-semibold flex-shrink-0 pt-2">{i + 1}.</span>
                      <textarea
                        value={t}
                        onChange={(e) => updateTakeaway(i, e.target.value)}
                        rows={2}
                        className="flex-1 text-sm px-2 py-1.5 border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                        data-testid={`synthesis-takeaway-input-${i}`}
                        placeholder={lang === "fr" ? "Point clé…" : "Takeaway…"}
                      />
                      <button
                        type="button"
                        onClick={() => removeTakeaway(i)}
                        className="mt-1 p-1 rounded text-rose-500 hover:bg-rose-50 flex-shrink-0"
                        data-testid={`synthesis-takeaway-remove-${i}`}
                        title={lang === "fr" ? "Supprimer ce point" : "Remove takeaway"}
                        aria-label={lang === "fr" ? "Supprimer ce point" : "Remove takeaway"}
                      >
                        <Trash size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : (
            takeaways.length > 0 && (
              <section className="border-t border-zinc-200 pt-4" data-testid="synthesis-takeaways">
                <h3 className="text-sm font-semibold text-zinc-700 mb-3">
                  {lang === "fr" ? "Points clés à retenir" : "Key takeaways"}
                </h3>
                <ul className="space-y-2">
                  {takeaways.map((t, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-zinc-700"
                      data-testid={`synthesis-takeaway-${i}`}
                    >
                      <span className="text-[#002FA7] font-semibold flex-shrink-0">{i + 1}.</span>
                      {/* Vincent B721 (Eric 2026-02-11) — Rendu markdown inline
                          (bold, italic, code) au lieu du texte brut. */}
                      <span dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(t) }} />
                    </li>
                  ))}
                </ul>
              </section>
            )
          )}
        </div>
      )}
      {/* B1067 (Eric 2026-09-04 SPEC STRICK PROD) — Historique des
          vérifications : qui a vérifié, quand, avec quel fournisseur, ce qui
          a été trouvé, et les corrections appliquées ensuite. */}
      {synthesis?.id && (
        <>
          {/* B1072 — Passages verrouillés + compte rendu de la dernière
              régénération : un verrou réappliqué en silence serait une
              modification invisible du contenu. */}
          <LockedPassagesList
            videoId={videoId}
            locks={locks}
            lockReport={synthesis?.content?.lock_report}
            onChanged={loadLocks}
            onOpenTranscript={onOpenTranscript}
            lang={lang}
          />
          <SynthesisVerificationHistory api={api} videoId={videoId} lang={lang} />
        </>
      )}
      {/* Vincent B943 (Eric 2026-02-14 SPEC STRICK PROD) — Modal fact-check. */}
      {synthesis?.id && (
        <SynthesisVerifyModal
          lang={lang}
          videoId={videoId}
          synthesisId={synthesis.id}
          isOpen={verifyOpen}
          onClose={() => setVerifyOpen(false)}
          onApplied={async (info) => {
            // Vincent B1021 (Eric 2026-02-26 SPEC STRICK PROD) —
            // Décrémente le compteur d'erreurs non corrigées du nombre
            // de corrections appliquées. Cas triviaux :
            //   - info absent (ancien callsite) → décrémente d'aucune
            //   - appliedCount >= unresolvedClaims → passe à 0
            const applied = Number(info?.appliedCount || 0);
            if (applied > 0) {
              setUnresolvedClaims((n) => Math.max(0, n - applied));
            }
            // Une correction appliquée modifie le body de la synthèse
            // → l'useEffect sur synthesis.content.body remettra
            // verifiedClean à false ET unresolvedClaims à 0 dès que le
            // parent aura rechargé. On force ici aussi pour ne pas
            // laisser une frame avec un check vert désynchronisé.
            setVerifiedClean(false);
            setSessionVerifiedAt(null);  // B1065 — repasse sur la date du serveur
            if (onRefresh) await onRefresh();
          }}
          onVerified={({ hasClaims, claimsCount, verifiedAt }) => {
            // Vincent B959 (Eric 2026-02-26 SPEC STRICK PROD) — Le check
            // vert doit s'afficher DÈS QUE la vérification est terminée,
            // qu'elle ait rapporté ou non des erreurs. L'ancienne logique
            // basée sur la négation de hasClaims ne flippait que sur 0
            // erreur, ce qui laissait le user devant une loupe même après
            // un run qui avait rapporté des claims — impossible de savoir
            // si la vérification avait tourné. Le reset "périmé" reste
            // piloté par l'useEffect sur synthesis.id/body/title (édition
            // ou régénération invalide la vérification).
            void hasClaims;
            setVerifiedClean(true);
            // Vincent B1021 — Snapshot du compteur d'erreurs pour le
            // badge « N » sur le bouton.
            setUnresolvedClaims(Number(claimsCount || 0));
            // B1065 — Date de la vérification, affichée immédiatement à côté
            // du check vert sans attendre que le parent recharge la synthèse.
            setSessionVerifiedAt(verifiedAt || new Date().toISOString());
          }}
        />
      )}

      {/* Vincent B1036 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
          flottant « + Réviser » qui apparaît au-dessus de la sélection
          dans le body de la synthèse. Rendu en position:absolute page.
          Cliquable seulement si canEditKnowledge (déjà filtré par le
          useEffect qui alimente selectionButton). */}
      {selectionButton && canEditKnowledge && (
        <button
          type="button"
          style={{ position: "fixed", top: selectionButton.top, left: selectionButton.left, transform: "translateX(-50%)", zIndex: 60 }}
          onMouseDown={(e) => {
            // preventDefault évite que la sélection soit effacée par le
            // click sur le bouton avant que setPendingSelectionText soit
            // appelé.
            e.preventDefault();
            setPendingSelectionText(selectionButton.text);
            setManualRevisionOpen(true);
            setSelectionButton(null);
          }}
          className="px-2.5 py-1.5 rounded-md bg-amber-600 text-white text-[11px] font-semibold shadow-lg hover:bg-amber-500 whitespace-nowrap"
          data-testid="synthesis-selection-add-revision-btn"
          aria-label="Ajouter une révision manuelle sur la sélection"
        >
          + Réviser cette phrase
        </button>
      )}

      <ManualRevisionModal
        open={manualRevisionOpen}
        onClose={() => setManualRevisionOpen(false)}
        videoId={videoId}
        originalText={pendingSelectionText}
        onSaved={async () => {
          if (onRefresh) await onRefresh();
        }}
      />
      {/* Tâche 1285 — Compte rendu des termes détectés dans la synthèse. */}
      <DictionaryGenerationModal
        open={!!dictResult}
        onClose={() => setDictResult(null)}
        result={dictResult}
        lang={lang}
        onSearchEnrichments={chercherEnrichissements}
        onApplyEnrichments={appliquerEnrichissements}
      />
    </div>
  );
}
